// AuthContext — REAL, contra Ecolab + backend de CoopNet. Reemplaza
// por completo el mock anterior (Firebase Google + usuarios de
// mockData.js). Ver INSTRUCTIVO_TURNSTILE_APPS_FINAL.md y
// SSO_CROSS_APP_INTEGRACION.md para el porqué de cada pieza.
//
// VISTA ACTIVA (selector de contexto). Un usuario puede tener más de una
// "vista" posible: SuperAdmin (toda la red), uno o varios roles con
// alcance sobre una entidad (AdminCooperativa de X, Contador de Y...) y/o
// ser afiliado. La vista define el nivel y la entidad con que se arman el
// menú y las pantallas. Reglas:
//   • Una sola vista posible → se usa sin preguntar.
//   • Varias y el usuario acaba de iniciar sesión → se le pregunta con
//     cuál quiere entrar (SelectorVistaDialog).
//   • Recarga de página → se conserva la última elegida (localStorage).
//   • Se cambia cuando quiera desde el menú del avatar ("Cambiar de vista").
// La vista solo cambia lo que se MUESTRA. Los permisos siguen siendo los
// roles reales, y el backend valida cada endpoint: un SuperAdmin dentro
// de la vista de una cooperativa conserva todos sus permisos.
//
// (Reemplaza el "hueco de diseño" anterior, que tomaba automáticamente el
// primer rol con alcance y no dejaba elegir ni volver a la vista global.)
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import { loginEcolab, EcolabAuthError } from '../services/ecolabAuth';
import { getToken, setToken } from '../services/apiClient';
import { meApi, organizacionApi } from '../services/coopnetApi';
import { NIVELES_SISTEMA } from '../utils/niveles';

const AuthContext = createContext(null);

const CONTEXTO_VACIO = { nivel: null, confederacion: null, federacion: null, cooperativa: null, afiliado: null };

// ── Vista guardada (sobrevive a recargas de página, no a un login nuevo) ──
const CLAVE_VISTA = (usuarioId) => `coopnet_vista_activa:${usuarioId}`;
const leerVistaGuardada = (usuarioId) => {
  try {
    return localStorage.getItem(CLAVE_VISTA(usuarioId));
  } catch {
    return null;
  }
};
const guardarVista = (usuarioId, vistaId) => {
  try {
    localStorage.setItem(CLAVE_VISTA(usuarioId), vistaId);
  } catch {
    /* sin storage disponible: la vista solo dura la sesión en memoria */
  }
};

/** id → nombre de cada entidad, para rotular las vistas. Si falla, se rotula genérico. */
const cargarNombres = async () => {
  const mapa = (arr) => new Map((Array.isArray(arr) ? arr : []).map((x) => [x.id, x.nombre]));
  const [confs, feds, coops] = await Promise.all([
    organizacionApi.listarConfederaciones().catch(() => []),
    organizacionApi.listarFederaciones().catch(() => []),
    organizacionApi.listarCooperativas().catch(() => []),
  ]);
  return { confederaciones: mapa(confs), federaciones: mapa(feds), cooperativas: mapa(coops) };
};

/**
 * Vistas posibles del usuario. Los roles que caen sobre la misma entidad
 * se agrupan en una sola vista (la vista es "dónde", los roles son "qué puede").
 */
const construirVistas = ({ perfil, roles, esSuperAdmin, nombres }) => {
  const vistas = [];

  if (esSuperAdmin || roles.some((r) => r.rol === 'SuperAdmin')) {
    vistas.push({
      id: 'global',
      nivel: NIVELES_SISTEMA.CONFEDERACION,
      titulo: 'Toda la red',
      detalle: 'SuperAdmin',
      roles: ['SuperAdmin'],
    });
  }

  const porEntidad = new Map();
  for (const r of roles) {
    // El rol Afiliado no administra su cooperativa: su vista es "Mi membresía"
    // (abajo), nunca el panel de la entidad.
    if (r.rol === 'Afiliado') continue;
    let clave;
    let nivel;
    let titulo;
    let ids;
    if (r.cooperativaId) {
      clave = `cooperativa:${r.cooperativaId}`;
      nivel = NIVELES_SISTEMA.COOPERATIVA;
      titulo = nombres.cooperativas.get(r.cooperativaId) || 'Cooperativa';
      ids = { cooperativaId: r.cooperativaId };
    } else if (r.federacionId) {
      clave = `federacion:${r.federacionId}`;
      nivel = NIVELES_SISTEMA.FEDERACION;
      titulo = nombres.federaciones.get(r.federacionId) || 'Federación';
      ids = { federacionId: r.federacionId };
    } else if (r.confederacionId) {
      clave = `confederacion:${r.confederacionId}`;
      nivel = NIVELES_SISTEMA.CONFEDERACION;
      titulo = nombres.confederaciones.get(r.confederacionId) || 'Confederación';
      ids = { confederacionId: r.confederacionId };
    } else {
      continue; // sin alcance (SuperAdmin): ya está cubierto por la vista global
    }
    if (!porEntidad.has(clave)) porEntidad.set(clave, { id: clave, nivel, titulo, ...ids, roles: [] });
    porEntidad.get(clave).roles.push(r.rol);
  }
  for (const v of porEntidad.values()) vistas.push({ ...v, detalle: v.roles.join(', ') });

  // Vista de afiliado: si el usuario está vinculado a un afiliado o tiene el
  // rol Afiliado. Sin vínculo, el panel muestra el aviso de "no vinculado".
  if (perfil.afiliadoId || roles.some((r) => r.rol === 'Afiliado')) {
    vistas.push({
      id: 'afiliado',
      nivel: NIVELES_SISTEMA.AFILIADO,
      titulo: 'Mi membresía',
      detalle: 'Afiliado',
      roles: ['Afiliado'],
    });
  }
  return vistas;
};

/** Carga la entidad de la vista (cooperativa + su federación, etc.) para armar el contexto. */
const resolverContextoDeVista = async (vista, perfil) => {
  const base = { ...CONTEXTO_VACIO, nivel: vista.nivel };
  try {
    if (vista.nivel === NIVELES_SISTEMA.COOPERATIVA) {
      const cooperativa = await organizacionApi.obtenerCooperativa(vista.cooperativaId);
      const federacion = cooperativa?.federacionId ? await organizacionApi.obtenerFederacion(cooperativa.federacionId) : null;
      return { ...base, federacion, cooperativa };
    }
    if (vista.nivel === NIVELES_SISTEMA.FEDERACION) {
      const federacion = await organizacionApi.obtenerFederacion(vista.federacionId);
      return { ...base, federacion };
    }
    if (vista.nivel === NIVELES_SISTEMA.AFILIADO) {
      if (!perfil?.afiliadoId) return base; // rol Afiliado aún sin vínculo
      const afiliado = await organizacionApi.obtenerAfiliado(perfil.afiliadoId);
      const cooperativa = await organizacionApi.obtenerCooperativa(afiliado.cooperativaId);
      const federacion = cooperativa ? await organizacionApi.obtenerFederacion(cooperativa.federacionId) : null;
      return { ...base, federacion, cooperativa, afiliado };
    }
    // Confederación (global o con alcance): aún no hay GET /confederaciones/{id}
    // individual — se agrega si hace falta.
    return base;
  } catch (err) {
    console.warn('No se pudo resolver el contexto organizacional de la vista:', err);
    return base;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contexto, setContexto] = useState(CONTEXTO_VACIO);

  const [vistas, setVistas] = useState([]);
  const [vistaActivaId, setVistaActivaId] = useState(null);
  const [necesitaSeleccion, setNecesitaSeleccion] = useState(false); // login con varias vistas: hay que elegir
  const [selectorAbierto, setSelectorAbierto] = useState(false); // abierto a voluntad ("Cambiar de vista")
  const perfilRef = useRef(null);

  const [resumenRed, setResumenRed] = useState(null);
  /** Totales y conteos reales de la red (cabecera del menú, dashboards). Informativo: si falla no rompe la sesión. */
  const recargarResumen = useCallback(async () => {
    try {
      setResumenRed(await organizacionApi.resumen());
    } catch (err) {
      console.warn('No se pudo cargar el resumen de la red:', err);
    }
  }, []);

  const reiniciarVistas = () => {
    perfilRef.current = null;
    setResumenRed(null);
    setVistas([]);
    setVistaActivaId(null);
    setNecesitaSeleccion(false);
    setSelectorAbierto(false);
  };

  /** Pone la vista como activa: recarga su entidad y actualiza contexto y campos de compatibilidad de `user`. */
  const aplicarVista = useCallback(async (vista) => {
    const ctx = await resolverContextoDeVista(vista, perfilRef.current);
    setContexto(ctx);
    setUser(
      (u) =>
        u && {
          ...u,
          nivel: ctx.nivel,
          cooperativaId: ctx.cooperativa?.id ?? null,
          federacionId: ctx.federacion?.id ?? null,
          confederacionId: ctx.confederacion?.id ?? null,
        }
    );
    setVistaActivaId(vista.id);
  }, []);

  /**
   * @param {object} jwtClaims
   * @param {{preguntar?: boolean}} [opciones] preguntar=true en un login nuevo:
   *   ignora la vista guardada y, si hay varias, obliga a elegir.
   */
  const construirSesion = useCallback(
    async (jwtClaims, { preguntar = false } = {}) => {
      // Secuencial, no Promise.all: en el PRIMER login de una cuenta
      // (todavía sin fila en usuarios_cache) dos requests concurrentes
      // pueden pisarse al aprovisionar. El backend ya tolera esa
      // carrera (UserProvisioningService la recupera sola), pero
      // evitarla aquí es más simple que depender de esa recuperación.
      const perfil = await meApi.perfil();
      const roles = await meApi.roles();
      const permisos = await meApi.permisos();
      perfilRef.current = perfil;

      const esSuperAdmin = Boolean(permisos?.esSuperAdmin);
      const nombres = await cargarNombres(); // ya con la cuenta aprovisionada
      await recargarResumen();

      const disponibles = construirVistas({ perfil, roles, esSuperAdmin, nombres });
      const guardada = preguntar ? null : leerVistaGuardada(perfil.id);
      const inicial = disponibles.find((v) => v.id === guardada) || disponibles[0] || null;
      const nombre = perfil.nombre || perfil.email || '';

      setUser({
        id: perfil.id,
        ecolabUserId: perfil.ecolabUserId,
        nombre,
        nombreCompleto: nombre,
        email: perfil.email,
        documento: perfil.documento,
        afiliadoId: perfil.afiliadoId,
        activo: perfil.activo,
        roles,
        esSuperAdmin,
        rolesEfectivos: permisos?.rolesEfectivos ?? [],
        rol: esSuperAdmin ? 'SuperAdmin' : roles[0]?.rol ?? null,
        // Compatibilidad: las pantallas anteriores leen estos campos
        // directamente de `user`. Los completa aplicarVista().
        nivel: inicial?.nivel ?? null,
        cooperativaId: null,
        federacionId: null,
        confederacionId: null,
      });
      setVistas(disponibles);

      if (inicial) await aplicarVista(inicial);
      else setContexto(CONTEXTO_VACIO);

      // Varias vistas y ninguna elegida todavía → preguntar.
      setNecesitaSeleccion(disponibles.length > 1 && !disponibles.some((v) => v.id === guardada));
      setSelectorAbierto(false);
    },
    [aplicarVista, recargarResumen]
  );

  /** Cambia de vista (desde el diálogo) y la recuerda para las recargas. */
  const seleccionarVista = useCallback(
    async (id) => {
      const vista = vistas.find((v) => v.id === id);
      if (!vista) return;
      await aplicarVista(vista);
      if (perfilRef.current) guardarVista(perfilRef.current.id, id);
      setNecesitaSeleccion(false);
      setSelectorAbierto(false);
    },
    [vistas, aplicarVista]
  );

  // Restaurar sesión desde el JWT guardado (recarga de página).
  useEffect(() => {
    (async () => {
      const token = getToken();
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp && decoded.exp * 1000 < Date.now()) {
          setToken(null);
          setLoading(false);
          return;
        }
        await construirSesion(decoded);
      } catch (err) {
        console.error('No se pudo restaurar la sesión:', err);
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [construirSesion]);

  /** @returns {{success:boolean, error?:string, turnstileError?:boolean}} */
  const login = async (email, password, turnstileToken) => {
    setLoading(true);
    try {
      const { token } = await loginEcolab(email, password, turnstileToken);
      setToken(token);
      const decoded = jwtDecode(token);
      await construirSesion(decoded, { preguntar: true });
      return { success: true };
    } catch (err) {
      setToken(null);
      setUser(null);
      setContexto(CONTEXTO_VACIO);
      reiniciarVistas();
      return {
        success: false,
        error: err.message || 'No se pudo iniciar sesión.',
        turnstileError: err instanceof EcolabAuthError && err.turnstileError,
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setContexto(CONTEXTO_VACIO);
    reiniciarVistas();
  };

  // user.roles es un array de { asignacionId, rol, confederacionId,
  // federacionId, cooperativaId } — no strings. hasRole por defecto
  // ignora el alcance (¿tiene este rol EN ALGUNA entidad?); pasa
  // cooperativaId para exigir que sea justo en esa cooperativa.
  // SuperAdmin (rol del ecosistema, verificado por el servidor en
  // /me/permisos) pasa cualquier verificación de rol en la interfaz —
  // la autorización real igual la hace el backend en cada endpoint.
  const hasRole = (rol, cooperativaId = undefined) =>
    Boolean(
      user?.esSuperAdmin ||
        user?.roles?.some((r) => r.rol === rol && (cooperativaId === undefined || r.cooperativaId === cooperativaId))
    );

  const hasAnyRole = (rolesEsperados) => rolesEsperados.some((r) => hasRole(r));

  // ── Compatibilidad con pantallas existentes (mock) ──────────────
  // hasPermission ya NO tiene el detalle fino de "permisos" por rol
  // que tenía el mock (rolInfo.permisos) — solo SuperAdmin pasa todo.
  // Las pantallas que dependan de un permiso puntual deben migrar a
  // hasRole/hasAnyRole con el/los coopnet_role que correspondan.
  const hasPermission = () => hasRole('SuperAdmin');

  const getNivelLabel = (nivel) => {
    const labels = {
      [NIVELES_SISTEMA.CONFEDERACION]: 'Confederación',
      [NIVELES_SISTEMA.FEDERACION]: 'Federación',
      [NIVELES_SISTEMA.COOPERATIVA]: 'Cooperativa',
      [NIVELES_SISTEMA.AFILIADO]: 'Afiliado',
    };
    return labels[nivel] || nivel;
  };

  const getNivelColor = (nivel) => {
    const colors = {
      [NIVELES_SISTEMA.CONFEDERACION]: '#7B1FA2',
      [NIVELES_SISTEMA.FEDERACION]: '#1565C0',
      [NIVELES_SISTEMA.COOPERATIVA]: '#2E7D32',
      [NIVELES_SISTEMA.AFILIADO]: '#FF8F00',
    };
    return colors[nivel] || '#666666';
  };

  const value = {
    user,
    loading,
    contexto,
    login,
    logout,
    hasRole,
    hasAnyRole,
    hasPermission,
    getNivelLabel,
    getNivelColor,
    isAuthenticated: !!user,
    // Vista activa
    vistas,
    vistaActivaId,
    necesitaSeleccion,
    selectorAbierto,
    setSelectorAbierto,
    seleccionarVista,
    resumenRed,
    recargarResumen,
    // Shortcuts de nivel (compatibilidad) — derivados de la vista activa.
    isConfederacion: contexto.nivel === NIVELES_SISTEMA.CONFEDERACION,
    isFederacion: contexto.nivel === NIVELES_SISTEMA.FEDERACION,
    isCooperativa: contexto.nivel === NIVELES_SISTEMA.COOPERATIVA,
    isAfiliado: contexto.nivel === NIVELES_SISTEMA.AFILIADO,
    NIVELES: NIVELES_SISTEMA,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export default AuthContext;
